import { useEffect, useState } from "react";
import type { AuthUser, SyncStatus } from "@ailan/contracts";
import { Sidebar } from "./components/Sidebar";
import { PatrolPage } from "./pages/PatrolPage";
import { KnowledgePage } from "./pages/KnowledgePage";
import { ExamPage } from "./pages/ExamPage";
import { OperationsModulePage } from "./pages/OperationsModulePage";
import { QualityInspectionPage } from "./pages/QualityInspectionPage";
import { ReviewsPage } from "./pages/ReviewsPage";
import { ProductInsightsPage } from "./pages/ProductInsightsPage";
import { TmallDashboardPage } from "./pages/TmallDashboardPage";
import { KuaishouDashboardPage } from "./pages/KuaishouDashboardPage";
import { PddDashboardPage } from "./pages/PddDashboardPage";
import { UnifiedDashboardPage } from "./pages/UnifiedDashboardPage";
import { DailyAgentComparisonPage } from "./pages/DailyAgentComparisonPage";
import { DouyinDashboardPage } from "./pages/DouyinDashboardPage";
import { TmallExperiencePage } from "./pages/TmallExperiencePage";
import { DouyinExperiencePage } from "./pages/DouyinExperiencePage";
import { KuaishouExperiencePage } from "./pages/KuaishouExperiencePage";
import { ServiceTicketsPage } from "./pages/ServiceTicketsPage";
import { isAppPage, type AppPage } from "./navigation";
import { moduleConfigs } from "./operations/moduleConfigs";
import { getCurrentUser, getHermesStatus, login, logout, syncHermes } from "./api/client";

function pageFromLocation(): AppPage {
  const hash = window.location.hash.slice(1);
  return isAppPage(hash) ? hash : "exam";
}

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [page, setPage] = useState<AppPage>(pageFromLocation);

  useEffect(() => {
    const syncPage = () => setPage(pageFromLocation());
    window.addEventListener("popstate", syncPage);
    window.addEventListener("hashchange", syncPage);
    return () => {
      window.removeEventListener("popstate", syncPage);
      window.removeEventListener("hashchange", syncPage);
    };
  }, []);

  useEffect(() => {
    void getCurrentUser()
      .then((nextUser) => {
        setUser(nextUser);
        return getHermesStatus().then(setSyncStatus);
      })
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  if (authLoading) return <div className="authScreen"><p>正在验证登录状态…</p></div>;
  if (!user) {
    return <LoginGate error={authError} onLogin={async (username, password) => {
      setAuthError("");
      try {
        const nextUser = await login(username, password);
        setUser(nextUser);
        setSyncStatus(await getHermesStatus());
      } catch (error) {
        setAuthError(error instanceof Error ? error.message : "登录失败");
      }
    }} />;
  }

  const navigate = (nextPage: AppPage) => {
    setPage(nextPage);
    window.history.pushState({}, "", `/#${nextPage}`);
  };

  const pageContent =
    page === "knowledge" ? (
      <KnowledgePage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "dashboard" ? (
      <UnifiedDashboardPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "daily-agent-comparison" ? (
      <DailyAgentComparisonPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "tmall" ? (
      <TmallDashboardPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "kuaishou" ? (
      <KuaishouDashboardPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "pdd" ? (
      <PddDashboardPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "douyin" ? (
      <DouyinDashboardPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "patrol" ? (
      <PatrolPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "experience" ? (
      <TmallExperiencePage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "experience-douyin" ? (
      <DouyinExperiencePage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "experience-kuaishou" ? (
      <KuaishouExperiencePage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "exam" ? (
      <ExamPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "quality" ? (
      <QualityInspectionPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "reviews" ? (
      <ReviewsPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "product-insights" ? (
      <ProductInsightsPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : page === "automation" ? (
      <ServiceTicketsPage onMenuOpen={() => setNavigationOpen(true)} />
    ) : moduleConfigs[page] ? (
      <OperationsModulePage
        page={page}
        onMenuOpen={() => setNavigationOpen(true)}
      />
    ) : (
      <ExamPage onMenuOpen={() => setNavigationOpen(true)} />
    );
  return (
    <div className="appShell">
      <Sidebar
        open={navigationOpen}
        current={page}
        onNavigate={navigate}
        onClose={() => setNavigationOpen(false)}
      />
      <button
        className={`mobileBackdrop ${navigationOpen ? "show" : ""}`}
        aria-label="关闭导航"
        onClick={() => setNavigationOpen(false)}
      />
      {pageContent}
      <div className="sessionBar">
        <span><strong>{user.displayName}</strong> · {user.role === "ADMIN" ? "管理员" : "只读"}</span>
        <span className={syncStatus?.ready ? "syncReady" : "syncPending"}>
          Hermes：{syncStatus?.lastRun?.status ?? (syncStatus?.configured ? "等待首次同步" : "未配置")}
        </span>
        {user.role === "ADMIN" && <button onClick={() => void syncHermes().then(setSyncStatus)}>立即同步</button>}
        <button onClick={() => void logout().finally(() => setUser(null))}>退出</button>
      </div>
    </div>
  );
}

function LoginGate({ error, onLogin }: { error: string; onLogin: (username: string, password: string) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return <main className="authScreen"><form className="authCard" onSubmit={(event) => {
    event.preventDefault();
    setSubmitting(true);
    void onLogin(username, password).finally(() => setSubmitting(false));
  }}>
    <span>健泽 · 爱兰客服</span>
    <h1>登录运营工作台</h1>
    <label>用户名<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></label>
    <label>密码<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
    {error && <p className="authError">{error}</p>}
    <button disabled={submitting}>{submitting ? "登录中…" : "登录"}</button>
  </form></main>;
}
